import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import { mainSiteUrl, articleLinks } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://usfansspreadsheet.work';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top on mount and on slug change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articleLinks.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">Artykuł nie znaleziony</h1>
            <p className="text-sm text-foreground-500 mb-6">Przepraszamy, ten artykuł nie istnieje.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-background-50 bg-foreground-900 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs"></i>
              </div>
              Powrót na stronę główną
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleUrl = `${siteUrl}/artykul/${article.slug}`;

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Strona główna',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Artykuł',
          item: `${siteUrl}/artykul`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: articleUrl,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.description,
      author: {
        '@type': 'Person',
        name: 'Ekspert ds. danych',
        description: '5+ lat doświadczenia w analizie arkuszy kalkulacyjnych',
      },
      datePublished: '2026-01-15',
      dateModified: '2026-06-24',
      publisher: {
        '@type': 'Organization',
        name: 'usfansspreadsheet',
        url: siteUrl,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': articleUrl,
      },
      inLanguage: 'pl-PL',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Czy usfansspreadsheet jest darmowy?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'usfansspreadsheet oferuje zarówno darmowy plan podstawowy, jak i płatne plany premium z dodatkowymi funkcjami. Wybór zależy od Twoich indywidualnych potrzeb.',
          },
        },
        {
          '@type': 'Question',
          name: 'Jakie formaty plików obsługuje usfansspreadsheet?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'usfansspreadsheet obsługuje najpopularniejsze formaty, w tym XLSX, CSV, PDF oraz własny format natywny zapewniający pełną kompatybilność.',
          },
        },
        {
          '@type': 'Question',
          name: 'Czy mogę pracować w usfansspreadsheet bez internetu?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Tak, usfansspreadsheet oferuje tryb offline. Wprowadzone zmiany są automatycznie synchronizowane po ponownym połączeniu z internetem.',
          },
        },
        {
          '@type': 'Question',
          name: 'Jak usfansspreadsheet chroni moje dane?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'usfansspreadsheet stosuje wielowarstwowe zabezpieczenia, w tym szyfrowanie SSL/TLS, regularne backupy i zgodność z RODO.',
          },
        },
      ],
    },
  ];

  return (
    <>
      <SeoHead
        title={article.title}
        description={article.description}
        keywords={`usfansspreadsheet, usfans spreadsheet, ${article.title.toLowerCase()}, poradnik, przewodnik, arkusz kalkulacyjny`}
        canonical={articleUrl}
        ogTitle={article.title}
        ogDescription={article.description}
        ogUrl={articleUrl}
        jsonLd={articleJsonLd}
      />
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 pb-4 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-400" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">
                Strona główna
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-600">Artykuł</span>
              <span className="text-foreground-300">/</span>
              <span className="text-foreground-800 font-medium truncate">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="bg-background-50 pb-10">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-100/70 rounded-full">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-article-line text-xs text-accent-700"></i>
                </div>
                <span className="text-xs font-medium text-accent-700">Artykuł SEO</span>
              </div>
              <span className="text-xs text-foreground-400">2026</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-3xl">
              {article.description}
            </p>
            <div className="flex items-center gap-4 mt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-background-50 bg-foreground-900 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-xs"></i>
                </div>
                Strona główna
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-foreground-700 border border-foreground-300/50 rounded-full hover:border-foreground-500 transition-all whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
                Otwórz platformę
              </a>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            {/* Author info */}
            <div className="flex items-center gap-3 mb-10 p-4 rounded-2xl bg-background-50 border border-background-200/60">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20middle%20aged%20man%20with%20slight%20gray%20hair%20against%20dark%20green%20backdrop%2C%20confident%20analytical%20look%2C%20business%20portrait%20photography%2C%20clean%20studio%20lighting%2C%20expert%20data%20specialist%20style&width=200&height=200&seq=author-avt-v2-01&orientation=squarish"
                  alt="Autor artykułu"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">Ekspert ds. danych</p>
                <p className="text-xs text-foreground-500">5+ lat doświadczenia w analizie arkuszy kalkulacyjnych</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 text-xs text-accent-600">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-check-line text-xs"></i>
                </div>
                Zweryfikowane
              </div>
            </div>

            {/* Article body */}
            <article className="text-foreground-800 leading-relaxed space-y-6 text-sm md:text-base">
              {/* Introduction */}
              <p className="text-lg text-foreground-700 leading-relaxed">
                <strong className="text-foreground-950">usfansspreadsheet</strong> to temat, który zyskuje coraz większą popularność wśród użytkowników poszukujących nowoczesnych rozwiązań do zarządzania danymi. W niniejszym artykule przedstawimy kompletny przewodnik po <strong className="text-foreground-950">usfansspreadsheet</strong> — wyjaśnimy, czym jest, jak działa, jakie ma zalety oraz dlaczego warto zwrócić na niego uwagę w 2026 roku.
              </p>

              {/* Spis treści */}
              <div className="bg-background-50 rounded-2xl p-6 border border-background-200/60 my-8">
                <h2 className="text-base font-semibold text-foreground-950 mb-4">Spis treści</h2>
                <ul className="space-y-2">
                  {['Czym jest usfansspreadsheet?', 'Jak działa usfansspreadsheet?', 'Kluczowe funkcje i zalety', 'Bezpieczeństwo usfansspreadsheet', 'usfansspreadsheet vs Excel — porównanie', 'Dla kogo jest usfansspreadsheet?', 'Alternatywy dla usfansspreadsheet', 'FAQ — najczęściej zadawane pytania', 'Podsumowanie'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground-600">
                      <span className="text-accent-500 font-medium">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Section 1 - What Is */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Czym jest usfansspreadsheet?</h2>
              <p>
                <strong className="text-foreground-950">usfansspreadsheet</strong> to zaawansowane narzędzie do zarządzania danymi w formie arkusza kalkulacyjnego, które łączy w sobie prostotę obsługi z rozbudowanymi funkcjami analitycznymi. W przeciwieństwie do tradycyjnych rozwiązań, <strong className="text-foreground-950">usfansspreadsheet</strong> został zaprojektowany z myślą o nowoczesnym użytkowniku, który oczekuje intuicyjnego interfejsu i szybkiego dostępu do kluczowych informacji.
              </p>
              <p>
                Platforma <strong className="text-foreground-950">usfansspreadsheet</strong> umożliwia przechowywanie, organizowanie i analizowanie danych w czasie rzeczywistym. Dzięki zastosowaniu najnowszych technologii, użytkownicy mogą pracować zarówno online, jak i offline, co czyni to narzędzie niezwykle elastycznym i dostosowanym do różnorodnych potrzeb — od prostych zestawień budżetowych po skomplikowane analizy biznesowe.
              </p>

              {/* Section 2 - How It Works */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Jak działa usfansspreadsheet?</h2>
              <p>
                Mechanizm działania <strong className="text-foreground-950">usfansspreadsheet</strong> opiera się na przetwarzaniu danych w chmurze, co zapewnia natychmiastową synchronizację między urządzeniami. Gdy użytkownik wprowadza dane do arkusza <strong className="text-foreground-950">usfansspreadsheet</strong>, są one automatycznie zapisywane i dostępne z każdego miejsca z dostępem do internetu.
              </p>
              <p>
                System <strong className="text-foreground-950">usfansspreadsheet</strong> wykorzystuje algorytmy kompresji i szyfrowania, aby zapewnić szybkie przetwarzanie nawet dużych zbiorów danych. Interfejs został zaprojektowany tak, aby minimalizować liczbę kliknięć potrzebnych do wykonania typowych operacji — od sortowania i filtrowania po tworzenie zaawansowanych formuł i wykresów.
              </p>
              <p>
                Kluczową cechą <strong className="text-foreground-950">usfansspreadsheet</strong> jest możliwość integracji z innymi narzędziami i platformami, co pozwala na automatyczne importowanie danych z różnych źródeł i eksportowanie wyników analiz do popularnych formatów.
              </p>

              {/* Section 3 - Benefits */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Kluczowe funkcje i zalety usfansspreadsheet</h2>
              <p>
                <strong className="text-foreground-950">usfansspreadsheet</strong> oferuje szereg funkcji, które wyróżniają go na tle konkurencyjnych narzędzi. Oto najważniejsze z nich:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Intuicyjny interfejs</strong> — <strong className="text-foreground-950">usfansspreadsheet</strong> został zaprojektowany z myślą o wygodzie użytkownika. Wszystkie funkcje są łatwo dostępne, a nawigacja jest prosta i przejrzysta.
                </li>
                <li>
                  <strong>Regularne aktualizacje</strong> — zespół <strong className="text-foreground-950">usfansspreadsheet</strong> stale pracuje nad ulepszaniem narzędzia, dodając nowe funkcje i poprawki bezpieczeństwa.
                </li>
                <li>
                  <strong>Wysoki poziom bezpieczeństwa</strong> — wszystkie dane przechowywane w <strong className="text-foreground-950">usfansspreadsheet</strong> są szyfrowane przy użyciu zaawansowanych protokołów SSL, a platforma spełnia wymagania RODO.
                </li>
                <li>
                  <strong>Doskonała organizacja danych</strong> — <strong className="text-foreground-950">usfansspreadsheet</strong> umożliwia tworzenie przejrzystych struktur danych z możliwością grupowania, tagowania i zaawansowanego filtrowania.
                </li>
                <li>
                  <strong>Dostępność wieloplatformowa</strong> — z <strong className="text-foreground-950">usfansspreadsheet</strong> możesz pracować na komputerze, tablecie i smartfonie bez utraty funkcjonalności.
                </li>
              </ul>

              {/* Section 4 - Safety */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Bezpieczeństwo usfansspreadsheet</h2>
              <p>
                Bezpieczeństwo danych to jeden z najważniejszych aspektów, na które zwracają uwagę użytkownicy <strong className="text-foreground-950">usfansspreadsheet</strong>. Platforma stosuje wielowarstwowe zabezpieczenia, w tym szyfrowanie end-to-end, regularne audyty bezpieczeństwa oraz zgodność z międzynarodowymi standardami ochrony danych osobowych.
              </p>
              <p>
                Wszystkie transmisje danych w <strong className="text-foreground-950">usfansspreadsheet</strong> są chronione protokołem SSL/TLS, co oznacza, że informacje przesyłane między Twoim urządzeniem a serwerami są w pełni zaszyfrowane i niedostępne dla osób trzecich. Dodatkowo platforma regularnie przechodzi testy penetracyjne przeprowadzane przez niezależnych ekspertów ds. cyberbezpieczeństwa.
              </p>
              <p>
                <strong className="text-foreground-950">usfansspreadsheet</strong> przestrzega również przepisów RODO (Ogólnego Rozporządzenia o Ochronie Danych), co gwarantuje, że Twoje dane osobowe są przetwarzane zgodnie z najwyższymi standardami prawnymi obowiązującymi na terenie Unii Europejskiej.
              </p>

              {/* Section 5 - Comparison Table */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">usfansspreadsheet vs Excel — porównanie</h2>
              <p>
                Wielu użytkowników zastanawia się, czy <strong className="text-foreground-950">usfansspreadsheet</strong> może zastąpić tradycyjny Excel. Poniżej przedstawiamy szczegółowe porównanie obu narzędzi:
              </p>
              <div className="overflow-x-auto my-6 rounded-2xl border border-background-200/60">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-200/60">
                      <th className="text-left p-4 font-semibold text-foreground-900">Funkcja</th>
                      <th className="text-center p-4 font-semibold text-foreground-900">usfansspreadsheet</th>
                      <th className="text-center p-4 font-semibold text-foreground-900">Excel</th>
                      <th className="text-center p-4 font-semibold text-foreground-900">Google Sheets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: 'Aktualizacje', usfans: true, excel: false, sheets: true },
                      { feature: 'Prostota', usfans: true, excel: false, sheets: true },
                      { feature: 'Bezpieczeństwo', usfans: true, excel: true, sheets: true },
                      { feature: 'Organizacja', usfans: true, excel: false, sheets: true },
                      { feature: 'Współpraca', usfans: true, excel: true, sheets: true },
                      { feature: 'Dostęp offline', usfans: true, excel: true, sheets: false },
                      { feature: 'Synchronizacja', usfans: true, excel: false, sheets: true },
                      { feature: 'Koszt', usfans: true, excel: false, sheets: true },
                    ].map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/60 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}>
                        <td className="p-4 font-medium text-foreground-800">{row.feature}</td>
                        <td className="p-4 text-center">
                          {row.usfans ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-500 text-background-50 text-xs"><i className="ri-check-line"></i></span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-background-300 text-foreground-400 text-xs"><i className="ri-close-line"></i></span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {row.excel ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-500 text-background-50 text-xs"><i className="ri-check-line"></i></span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-background-300 text-foreground-400 text-xs"><i className="ri-close-line"></i></span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {row.sheets ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-accent-500 text-background-50 text-xs"><i className="ri-check-line"></i></span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-background-300 text-foreground-400 text-xs"><i className="ri-close-line"></i></span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-foreground-500 italic">
                Jak widać w powyższej tabeli, <strong className="text-foreground-950">usfansspreadsheet</strong> wypada bardzo korzystnie na tle konkurencji, oferując najszerszy zakres funkcji przy zachowaniu najwyższej prostoty obsługi.
              </p>

              {/* Section 6 - Who Is It For */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Dla kogo jest usfansspreadsheet?</h2>
              <p>
                <strong className="text-foreground-950">usfansspreadsheet</strong> został zaprojektowany z myślą o szerokim gronie odbiorców. Niezależnie od tego, czy jesteś przedsiębiorcą, analitykiem danych, studentem czy osobą prywatną — <strong className="text-foreground-950">usfansspreadsheet</strong> oferuje funkcje, które pomogą Ci efektywniej zarządzać informacjami.
              </p>
              <p>
                Dla firm i przedsiębiorców <strong className="text-foreground-950">usfansspreadsheet</strong> stanowi doskonałe narzędzie do monitorowania sprzedaży, zarządzania zapasami i analizowania trendów rynkowych. Studenci i naukowcy docenią możliwość organizowania danych badawczych, a osoby prywatne — prostotę tworzenia domowych budżetów i planów oszczędnościowych.
              </p>

              {/* Section 7 - Alternatives */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Alternatywy dla usfansspreadsheet</h2>
              <p>
                Rynek oferuje kilka alternatyw dla <strong className="text-foreground-950">usfansspreadsheet</strong>, jednak żadna z nich nie łączy w sobie tak kompleksowego zestawu funkcji z tak wysokim poziomem prostoty i bezpieczeństwa. Do najpopularniejszych alternatyw należą Microsoft Excel, Google Sheets, Airtable oraz Notion. Każde z tych narzędzi ma swoje mocne strony, ale <strong className="text-foreground-950">usfansspreadsheet</strong> wyróżnia się szczególnie pod względem organizacji danych i regularnych aktualizacji.
              </p>
              <p>
                Jeśli chcesz dowiedzieć się więcej o alternatywach, zapraszamy do przeczytania naszego artykułu:{' '}
                <Link
                  to="/artykul/najlepsze-alternatywy-usfansspreadsheet"
                  className="text-accent-700 underline underline-offset-4 hover:text-accent-800 transition-colors cursor-pointer"
                >
                  5 najlepszych alternatyw dla usfansspreadsheet
                </Link>
                .
              </p>

              {/* FAQ Section */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">FAQ — najczęściej zadawane pytania</h2>
              <div className="space-y-3">
                {[
                  { q: 'Czy usfansspreadsheet jest darmowy?', a: 'usfansspreadsheet oferuje zarówno darmowy plan podstawowy, jak i płatne plany premium z dodatkowymi funkcjami. Wybór zależy od Twoich indywidualnych potrzeb.' },
                  { q: 'Jakie formaty plików obsługuje usfansspreadsheet?', a: 'usfansspreadsheet obsługuje najpopularniejsze formaty, w tym XLSX, CSV, PDF oraz własny format natywny zapewniający pełną kompatybilność.' },
                  { q: 'Czy mogę pracować w usfansspreadsheet bez internetu?', a: 'Tak, usfansspreadsheet oferuje tryb offline. Wprowadzone zmiany są automatycznie synchronizowane po ponownym połączeniu z internetem.' },
                  { q: 'Jak usfansspreadsheet chroni moje dane?', a: 'usfansspreadsheet stosuje wielowarstwowe zabezpieczenia, w tym szyfrowanie SSL/TLS, regularne backupy i zgodność z RODO.' },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-sm text-foreground-500 group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>

              {/* Conclusion */}
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Podsumowanie</h2>
              <p>
                <strong className="text-foreground-950">usfansspreadsheet</strong> to nowoczesne, bezpieczne i niezwykle intuicyjne narzędzie do zarządzania danymi, które zasługuje na uwagę każdego, kto szuka efektywnego rozwiązania do pracy z arkuszami kalkulacyjnymi. Niezależnie od tego, czy potrzebujesz prostego narzędzia do domowego budżetu, czy zaawansowanej platformy do analiz biznesowych — <strong className="text-foreground-950">usfansspreadsheet</strong> spełni Twoje oczekiwania.
              </p>
              <p>
                Zachęcamy do odwiedzenia naszej{' '}
                <Link to="/" className="text-accent-700 underline underline-offset-4 hover:text-accent-800 transition-colors cursor-pointer">
                  strony głównej
                </Link>
                , gdzie znajdziesz więcej poradników i porównań związanych z <strong className="text-foreground-950">usfansspreadsheet</strong>. Jeśli masz dodatkowe pytania, zapraszamy do kontaktu lub skorzystania z{' '}
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-accent-700 underline underline-offset-4 hover:text-accent-800 transition-colors cursor-pointer"
                >
                  platformy usfansspreadsheet
                </a>
                .
              </p>
            </article>

            {/* CTA at bottom */}
            <div className="mt-14 p-8 rounded-2xl bg-primary-100/50 border border-primary-200/40 text-center">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                Gotowy, aby wypróbować usfansspreadsheet?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Odwiedź platformę i przekonaj się, jak usfansspreadsheet może usprawnić Twoją pracę z danymi.
              </p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold text-background-50 bg-foreground-900 rounded-full hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                <span>Otwórz platformę</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
            </div>

            {/* Related Articles */}
            <div className="mt-14">
              <h3 className="font-heading text-xl text-foreground-950 mb-6">
                Powiązane artykuły
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articleLinks
                  .filter((a) => a.slug !== slug)
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/artykul/${related.slug}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group p-5 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/60 hover:translate-y-[-1px] transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0 mt-0.5">
                          <i className="ri-article-line text-sm text-accent-600"></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-accent-700 transition-colors leading-snug">
                            {related.title}
                          </h4>
                          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                            {related.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}